import axios from 'axios'
import { config } from '../../config'


const doloresProductionBackendUrl = config.DOLORES_PRODUCTION_BACKEND_URL
const hrClockRequestConfig = {
    timeout: 15000
}
const growthUsersCacheTtl = 5 * 60 * 1000
const teamUsersCacheTtl = 5 * 60 * 1000
let growthUsersCache = {
    users: null,
    expiresAt: 0,
    pending: null
}
let teamUsersCache = {
    users: null,
    expiresAt: 0,
    pending: null
}

// Reads the Dolores subscription identifier used by seller dropdowns.
export const read_dolores_user_sub_id = (user = {}) => {
    return `${user?.subId || user?.sub_id || user?.subscriptionId || user?.subscription_id || user?.subscription?.subId || user?.subscription?.id || user?.subscription?._id || user?.sub || user?.userId || user?.user_id || user?.id || user?._id || ''}`.trim()
}

// Reads the Dolores user identifier used for avatars and compatibility matching.
export const read_dolores_user_id = (user = {}) => {
    return `${user?.userId || user?.user_id || user?.id || user?._id || user?.sub || ''}`.trim()
}

// Reads whether the HR clock payload contains enough data to be trusted.
const read_hr_clock_payload_loaded = (clockPayload = {}) => {
    const clock = clockPayload?.clock || {}
    const remainingSeconds = Number(clockPayload?.remainingSeconds || clock?.remainingSeconds || 0)
    const totalSeconds = Number(clockPayload?.totalSeconds || clock?.totalSeconds || 0)

    return Boolean(
        clockPayload?.loaded === true
        || clockPayload?.clockId
        || clockPayload?.subId
        || clockPayload?.memberKey
        || clockPayload?.userId
        || clockPayload?.username
        || clockPayload?.email
        || clockPayload?.clockDate
        || clockPayload?.startedAt
        || clockPayload?.firstStartedAt
        || clockPayload?.pausedAt
        || clockPayload?.completedAt
        || clock?.clockId
        || clock?.clockDate
        || clock?.startedAt
        || clock?.firstStartedAt
        || clock?.pausedAt
        || clock?.completedAt
        || remainingSeconds > 0
        || totalSeconds > 0
    )
}

// Builds the member identity query used by the Dolores HR clock endpoints.
const read_hr_clock_params = (userState = {}, member = {}) => {
    const rawUser = userState?.user || userState || {}
    const subId = `${member?.subId || member?.subscriptionId || rawUser?.subId || rawUser?.subscriptionId || ''}`.trim()
    const userId = `${member?.userId || member?.id || member?._id || rawUser?.userId || rawUser?.sub || rawUser?.id || ''}`.trim()
    const email = `${member?.email || rawUser?.email || ''}`.trim()
    const username = `${member?.username || rawUser?.username || rawUser?.preferred_username || rawUser?.preferredUsername || ''}`.trim()

    return {
        subId,
        userId,
        email,
        username
    }
}

// Converts clock query params into the EventSource query string.
const read_hr_clock_query_string = (clockParams = {}) => {
    const searchParams = new URLSearchParams()

    Object.keys(clockParams).forEach((paramKey) => {
        const paramValue = `${clockParams[paramKey] || ''}`.trim()

        if (paramValue) {
            searchParams.set(paramKey, paramValue)
        }
    })

    return searchParams.toString()
}

// Normalizes the HR clock response so Sales can block with one stable shape.
const read_safe_hr_member_clock = (clockPayload = {}) => {
    const clock = clockPayload?.clock || {}
    const loaded = read_hr_clock_payload_loaded(clockPayload)
    const ticking = Boolean(clockPayload?.ticking || clock?.ticking)
    const overtime = Boolean(clockPayload?.overtime || clock?.overtime)

    return {
        clockId: clockPayload?.clockId || '',
        subId: clockPayload?.subId || clockPayload?.member?.subId || '',
        memberKey: clockPayload?.memberKey || '',
        userId: clockPayload?.userId || '',
        username: clockPayload?.username || '',
        email: clockPayload?.email || '',
        clockDate: clockPayload?.clockDate || '',
        started: Boolean(clockPayload?.started || clockPayload?.firstStartedAt || clockPayload?.startedAt),
        ticking,
        overtime,
        clock: {
            ticking,
            overtime
        },
        remainingSeconds: Number(clockPayload?.remainingSeconds || 0),
        totalSeconds: Number(clockPayload?.totalSeconds || 0),
        firstStartedAt: clockPayload?.firstStartedAt || '',
        startedAt: clockPayload?.startedAt || '',
        pausedAt: clockPayload?.pausedAt || '',
        completedAt: clockPayload?.completedAt || '',
        snapshotAt: clockPayload?.snapshotAt || new Date().toISOString(),
        loaded
    }
}

// Reads Dolores team members from the production HR USERS API.
export const read_dolores_users = async () => {
    try {
        const response = await axios.get(`${doloresProductionBackendUrl}/admins/user-export`)

        return response.data?.data?.users || []
    } catch (error) {
        console.error('GET DOLORES USERS')
        return []
    }
}

// Reads every current Dolores team member as a valid Sales seller.
export const read_dolores_team_users = async () => {
    if (teamUsersCache.users && teamUsersCache.expiresAt > Date.now()) {
        return teamUsersCache.users
    }

    if (teamUsersCache.pending) {
        return teamUsersCache.pending
    }

    teamUsersCache.pending = read_dolores_users().then((users) => {
        const teamUsers = (users || []).map((user) => read_dolores_seller_user(user))

        teamUsersCache = {
            users: teamUsers,
            expiresAt: Date.now() + teamUsersCacheTtl,
            pending: null
        }

        return teamUsers
    }).catch((error) => {
        teamUsersCache.pending = null
        throw error
    })

    return teamUsersCache.pending
}

// Normalizes HR department names so app boards can match COMPANY exports safely.
export const read_dolores_department_key = (department) => {
    const departmentName = department?.name || department?.department || department

    return `${departmentName || ''}`.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
}

// Builds the standard Erised avatar URL used by HR and other Doitforme apps.
export const read_dolores_user_avatar_url = (user) => {
    const explicitAvatarUrl = user?.avatarUrl || user?.profileImage || user?.image || user?.picture || user?.photo
    const userId = read_dolores_user_id(user)

    if (explicitAvatarUrl) {
        return explicitAvatarUrl
    }

    if (userId) {
        return `${config.minio}/erisedev/${userId}/avatar.png`
    }

    return ''
}

// Normalizes Dolores users before they are used as Growth sellers.
export const read_dolores_seller_user = (user = {}) => {
    const subId = read_dolores_user_sub_id(user)
    const userId = read_dolores_user_id(user)

    return {
        ...user,
        subId,
        subscriptionId: user?.subscriptionId || user?.subscription_id || subId,
        userId,
        id: userId,
        username: user?.username || user?.preferred_username || user?.preferredUsername || user?.email || '',
        firstname: user?.firstname || user?.firstName || '',
        lastname: user?.lastname || user?.lastName || '',
        email: user?.email || '',
        avatarUrl: read_dolores_user_avatar_url({
            ...user,
            userId
        })
    }
}

// Reads Dolores departments and roles from the production HR COMPANY API.
export const read_dolores_company = async () => {
    try {
        const response = await axios.get(`${doloresProductionBackendUrl}/admins/company-export`)

        return response.data?.data || {}
    } catch (error) {
        console.error('GET DOLORES COMPANY')
        return {}
    }
}

// Reads the saved COMPANY department that represents Growth.
export const read_dolores_growth_department = (companyExport) => {
    const departments = companyExport?.departments || []

    return departments.find((department) => {
        return read_dolores_department_key(department?.name).includes('growth')
    }) || null
}

// Checks whether a Dolores user belongs to the Growth department.
export const read_is_growth_department_user = (user, growthDepartment) => {
    const growthDepartmentKey = read_dolores_department_key(growthDepartment?.name || 'growth')
    const userDepartmentKey = read_dolores_department_key(user?.department || user?.departmentName)

    return userDepartmentKey === growthDepartmentKey || userDepartmentKey.includes('growth')
}

// Reads both production HR exports in the same request cycle.
export const read_dolores_hr_exports = async () => {
    const [users, company] = await Promise.all([
        read_dolores_users(),
        read_dolores_company()
    ])

    return {
        users,
        company
    }
}

// Reads actual HR members assigned to the Growth department.
export const read_dolores_growth_users = async () => {
    if (growthUsersCache.users && growthUsersCache.expiresAt > Date.now()) {
        return growthUsersCache.users
    }

    if (growthUsersCache.pending) {
        return growthUsersCache.pending
    }

    growthUsersCache.pending = read_dolores_hr_exports().then((hrExports) => {
        const growthDepartment = read_dolores_growth_department(hrExports.company)
        const users = hrExports.users
            .filter((user) => read_is_growth_department_user(user, growthDepartment))
            .map((user) => read_dolores_seller_user(user))

        growthUsersCache = {
            users,
            expiresAt: Date.now() + growthUsersCacheTtl,
            pending: null
        }

        return users
    }).catch((error) => {
        growthUsersCache.pending = null
        throw error
    })

    return growthUsersCache.pending
}

// Checks if a Dolores user belongs to the configured company-head department.
export const read_is_company_head_user = (user, companyExport) => {
    return user.department === companyExport.company_head_department
}

// Checks if a Dolores user has the head role for their selected department.
export const read_is_department_head_user = (user, companyExport) => {
    const companyRoles = companyExport.roles || []

    return companyRoles.some((roleRow) => {
        return roleRow.department === user.department
            && roleRow.role === user.role
            && roleRow.department_head === true
    })
}

// Combines USER and COMPANY exports with the head flags downstream screens need.
export const read_dolores_people_with_head_flags = async () => {
    const users = await read_dolores_users()
    const companyExport = await read_dolores_company()

    return users.map((user) => {
        return {
            ...user,
            company_head: read_is_company_head_user(user, companyExport),
            department_head: read_is_department_head_user(user, companyExport)
        }
    })
}

// Reads the current HR clock snapshot for one member.
export const get_hr_member_clock = async (userState, member = {}) => {
    const clockParams = read_hr_clock_params(userState, member)

    try {
        const response = await axios.get(`${doloresProductionBackendUrl}/admins/clocks/member`, {
            ...hrClockRequestConfig,
            params: clockParams
        })

        return read_safe_hr_member_clock(response?.data?.data || {})
    } catch (error) {
        console.error('GET HR MEMBER CLOCK')
        return { loaded: false }
    }
}

// Opens the HR clock live stream and pushes normalized snapshots to the caller.
export const open_hr_member_clock_events = (userState, member = {}, onClock = () => {}) => {
    if (typeof window === 'undefined' || !window.EventSource) {
        return () => {}
    }

    const clockParams = read_hr_clock_params(userState, member)
    const queryString = read_hr_clock_query_string(clockParams)
    const streamUrl = `${doloresProductionBackendUrl}/admins/clocks/member/events${queryString ? `?${queryString}` : ''}`
    const eventSource = new window.EventSource(streamUrl)

    eventSource.addEventListener('clock', (event) => {
        try {
            const clockPayload = JSON.parse(event?.data || '{}')
            onClock(read_safe_hr_member_clock(clockPayload))
        } catch (error) {
            console.error('HR MEMBER CLOCK EVENT')
        }
    })

    eventSource.onerror = () => {}

    return () => {
        eventSource.close()
    }
}

// Sends pause/resume to HR and returns the updated clock snapshot.
const send_hr_member_clock_action = async (userState, member = {}, action = '') => {
    const clockParams = read_hr_clock_params(userState, member)

    try {
        const response = await axios.post(`${doloresProductionBackendUrl}/admins/clocks/member/${action}`, {
            member: clockParams,
            source: 'leads'
        }, hrClockRequestConfig)

        return read_safe_hr_member_clock(response?.data?.data || {})
    } catch (error) {
        console.error(`POST HR MEMBER CLOCK ${action}`)
        return null
    }
}

export const pause_hr_member_clock = (userState, member = {}) => {
    return send_hr_member_clock_action(userState, member, 'pause')
}

export const resume_hr_member_clock = (userState, member = {}) => {
    return send_hr_member_clock_action(userState, member, 'resume')
}
